import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  X,
  Pin,
  Trash2,
  Edit3,
  Check,
  Phone,
  Video,
  Calendar,
  Clock,
  StickyNote,
  MessageSquare,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { CRMNote, NoteType } from '../../types/notes';
import { NOTE_TYPE_LABELS, NOTE_TYPE_COLORS } from '../../types/notes';
import { Markdown } from '../ui/Markdown';
import { useAskOtto } from '../../hooks/useAskOtto';

interface NoteDetailModalProps {
  note: CRMNote;
  onClose: () => void;
  onSave?: (content: string) => void;
  onPin?: () => void;
  onDelete?: () => void;
  readOnly?: boolean;
}

export function NoteDetailModal({
  note,
  onClose,
  onSave,
  onPin,
  onDelete,
  readOnly = false,
}: NoteDetailModalProps) {
  const [content, setContent] = useState(note.content);
  const [isEditing, setIsEditing] = useState(false);
  const { prefill: askOttoPrefill } = useAskOtto();

  const getNoteIcon = (type: NoteType) => {
    switch (type) {
      case 'call_note':
        return <Phone className="w-4 h-4" />;
      case 'meeting_note':
        return <Video className="w-4 h-4" />;
      case 'follow_up':
        return <Calendar className="w-4 h-4" />;
      case 'reminder':
        return <Clock className="w-4 h-4" />;
      default:
        return <StickyNote className="w-4 h-4" />;
    }
  };

  const handleSave = () => {
    if (content.trim() !== note.content && onSave) {
      onSave(content.trim());
    }
    setIsEditing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl max-w-lg w-full shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${NOTE_TYPE_COLORS[note.note_type]}`}>
              {getNoteIcon(note.note_type)}
            </div>
            <div>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${NOTE_TYPE_COLORS[note.note_type]}`}>
                {NOTE_TYPE_LABELS[note.note_type]}
              </span>
              <p className="text-xs text-neutral-500 mt-0.5">
                {formatDistanceToNow(new Date(note.created_at), { addSuffix: true, locale: fr })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onPin && (
              <button
                onClick={onPin}
                className={`p-1.5 rounded transition-colors hover:bg-neutral-100 ${note.is_pinned ? 'text-amber-500' : 'text-neutral-400'}`}
                title={note.is_pinned ? 'Désépingler' : 'Épingler'}
              >
                <Pin className={`w-4 h-4 ${note.is_pinned ? 'fill-current' : ''}`} />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4">
          {isEditing && !readOnly ? (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              autoFocus
              className="w-full min-h-[200px] p-3 border border-neutral-200 rounded-lg focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF]/20 outline-none resize-none transition-all"
              data-sensitive-text=""
            />
          ) : (
            <div className="min-h-[120px] p-3">
              {note.title && (
                <h3 className="text-base font-semibold text-neutral-900 mb-2">{note.title}</h3>
              )}
              <div data-sensitive-text="">
                <Markdown className="text-sm text-neutral-700">{note.content}</Markdown>
              </div>
            </div>
          )}

          {note.tags && note.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {note.tags.map((tag) => (
                <span key={tag} className="px-2 py-0.5 bg-neutral-100 text-neutral-600 text-xs rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Contact/Account info if available */}
          {(note.contact || note.account) && (
            <div className="mt-4 pt-3 border-t border-neutral-100 text-xs text-neutral-500">
              {note.contact && (
                <span className="mr-3">
                  Contact: {note.contact.first_name} {note.contact.last_name}
                </span>
              )}
              {note.account && (
                <span>Compte: {note.account.name}</span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-100 bg-neutral-50">
          <div className="flex items-center gap-2">
            {!readOnly && (
              <button
                onClick={() => {
                  // « Nouvelle mission » avec la note citée par sa clé ; Gilles écrit sa remarque à la
                  // suite (plus de chat, 12/10/2026).
                  const day = new Date(note.created_at).toLocaleDateString('fr-FR');
                  askOttoPrefill(`À propos de ma note du ${day} (note_id: ${note.id}) : `, note.account_id);
                  onClose();
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[#0066FF] hover:bg-blue-50 rounded-lg text-sm transition-colors"
              >
                <MessageSquare className="w-4 h-4" />
                En parler à Otto
              </button>
            )}
            {onDelete && !readOnly && (
              <button
                onClick={onDelete}
                className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isEditing && !readOnly ? (
              <>
                <button
                  onClick={() => { setContent(note.content); setIsEditing(false); }}
                  className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg text-sm transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={!content.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-[#0066FF] text-white rounded-lg text-sm font-medium hover:bg-[#0052CC] disabled:bg-neutral-300 transition-colors"
                >
                  <Check className="w-4 h-4" />
                  Enregistrer
                </button>
              </>
            ) : !readOnly && onSave ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-[#0066FF] text-white rounded-lg text-sm font-medium hover:bg-[#0052CC] transition-colors"
              >
                <Edit3 className="w-4 h-4" />
                Modifier
              </button>
            ) : (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-neutral-100 text-neutral-700 hover:bg-neutral-200 rounded-lg text-sm transition-colors"
              >
                Fermer
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
